import { useEffect, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useToast } from '../../contexts/ToastContext'
import AdminLayout from '../../components/admin/AdminLayout'
import ConfirmDialog from '../../components/admin/ConfirmDialog'
import { supabase } from '../../lib/supabase'
import { 
  Search, 
  Filter, 
  Shield, 
  Trash2, 
  Pin, 
  PinOff,
  AlertTriangle,
  Eye,
  CheckCircle,
  XCircle
} from 'lucide-react'

interface ForumPost {
  id: string
  user_id: string
  content: string
  upvotes: number
  hearts: number
  is_pinned: boolean
  is_inappropriate: boolean
  created_at: string
  profiles?: {
    email: string
    full_name: string
  }
}

export default function Moderation() {
  const { hasPermission } = useAuth()
  const toast = useToast()
  const [posts, setPosts] = useState<ForumPost[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean
    postId: string | null
    action: 'delete' | null
  }>({ isOpen: false, postId: null, action: null })

  useEffect(() => {
    loadPosts()
  }, [])

  const loadPosts = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('forum_posts')
        .select('*, profiles(email, full_name)')
        .order('created_at', { ascending: false })
        .limit(100)

      if (error) throw error
      setPosts(data || [])
    } catch (error: any) {
      console.error('Error loading posts:', error)
      toast.error('Error al cargar posts')
    } finally {
      setLoading(false)
    }
  }

  const handleTogglePin = async (postId: string, currentPinned: boolean) => {
    if (!hasPermission('moderate_forum')) {
      toast.error('No tienes permisos para moderar')
      return
    }

    try {
      const { error } = await supabase
        .from('forum_posts')
        .update({ is_pinned: !currentPinned })
        .eq('id', postId)

      if (error) throw error
      toast.success(currentPinned ? 'Post desanclado' : 'Post anclado exitosamente')
      loadPosts()
    } catch (error: any) {
      console.error('Error toggling pin:', error)
      toast.error(`Error: ${error.message}`)
    }
  }

  const handleMarkInappropriate = async (postId: string, currentInappropriate: boolean) => {
    if (!hasPermission('moderate_forum')) {
      toast.error('No tienes permisos para moderar')
      return
    }

    try {
      const { error } = await supabase
        .from('forum_posts')
        .update({ is_inappropriate: !currentInappropriate })
        .eq('id', postId)

      if (error) throw error
      toast.success(currentInappropriate ? 'Marcado como apropiado' : 'Marcado como inapropiado')
      loadPosts()
    } catch (error: any) {
      console.error('Error marking inappropriate:', error)
      toast.error(`Error: ${error.message}`)
    }
  }

  const handleDeletePost = (postId: string) => {
    if (!hasPermission('moderate_forum')) {
      toast.error('No tienes permisos para moderar')
      return
    }

    setConfirmDialog({ isOpen: true, postId, action: 'delete' })
  }

  const confirmDelete = async () => {
    if (!confirmDialog.postId) return

    try {
      const { error } = await supabase
        .from('forum_posts')
        .delete()
        .eq('id', confirmDialog.postId)

      if (error) throw error
      toast.success('Post eliminado exitosamente')
      loadPosts()
    } catch (error: any) {
      console.error('Error deleting post:', error)
      toast.error(`Error: ${error.message}`)
    } finally {
      setConfirmDialog({ isOpen: false, postId: null, action: null })
    }
  }

  const filteredPosts = posts.filter(post => {
    const matchesSearch =
      post.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.profiles?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.profiles?.full_name?.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesFilter =
      filterStatus === 'all' ||
      (filterStatus === 'inappropriate' && post.is_inappropriate) ||
      (filterStatus === 'pinned' && post.is_pinned) ||
      (filterStatus === 'normal' && !post.is_inappropriate && !post.is_pinned)

    return matchesSearch && matchesFilter
  })

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Moderación</h1>
          <p className="text-gray-600">Gestiona el contenido del foro y comentarios</p>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar en posts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
              />
            </div>

            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent appearance-none"
              >
                <option value="all">Todos los posts</option>
                <option value="normal">Posts normales</option>
                <option value="inappropriate">Marcados como inapropiados</option>
                <option value="pinned">Posts anclados</option>
              </select>
            </div>
          </div>
          
          <div className="mt-4 text-sm text-gray-600">
            Mostrando {filteredPosts.length} de {posts.length} posts
          </div>
        </div>

        {/* Lista de Posts */}
        <div className="space-y-4">
          {loading ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-education mx-auto"></div>
              <p className="text-gray-600 mt-2">Cargando posts...</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No se encontraron posts</p>
            </div>
          ) : (
            filteredPosts.map((post) => (
              <div 
                key={post.id} 
                className={`bg-white rounded-lg shadow-md p-6 ${
                  post.is_inappropriate ? 'border-2 border-red-300' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Usuario */}
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="font-medium text-gray-900">
                        {post.profiles?.full_name || 'Usuario sin nombre'}
                      </span>
                      <span className="text-sm text-gray-500">
                        {post.profiles?.email}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(post.created_at).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>

                    {/* Contenido */}
                    <p className="text-gray-700 mb-3">{post.content}</p>

                    {/* Estadísticas y badges */}
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span>Upvotes: {post.upvotes}</span>
                      <span>Hearts: {post.hearts}</span>
                      
                      {post.is_pinned && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-semibold flex items-center space-x-1">
                          <Pin className="h-3 w-3" />
                          <span>Anclado</span>
                        </span>
                      )}
                      
                      {post.is_inappropriate && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-semibold flex items-center space-x-1">
                          <AlertTriangle className="h-3 w-3" />
                          <span>Inapropiado</span>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Botones de acción */}
                  <div className="flex items-center space-x-2 ml-4">
                    {hasPermission('moderate_forum') && (
                      <>
                        {/* Toggle Pin */}
                        <button
                          onClick={() => handleTogglePin(post.id, post.is_pinned)}
                          className={`p-2 rounded-lg ${
                            post.is_pinned
                              ? 'text-yellow-600 hover:bg-yellow-50'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                          title={post.is_pinned ? 'Desanclar' : 'Anclar'}
                        >
                          {post.is_pinned ? (
                            <PinOff className="h-4 w-4" />
                          ) : (
                            <Pin className="h-4 w-4" />
                          )}
                        </button>

                        {/* Toggle Inappropriate */}
                        <button
                          onClick={() => handleMarkInappropriate(post.id, post.is_inappropriate)}
                          className={`p-2 rounded-lg ${
                            post.is_inappropriate
                              ? 'text-green-600 hover:bg-green-50'
                              : 'text-orange-600 hover:bg-orange-50'
                          }`}
                          title={post.is_inappropriate ? 'Marcar como apropiado' : 'Marcar como inapropiado'}
                        >
                          {post.is_inappropriate ? (
                            <CheckCircle className="h-4 w-4" />
                          ) : (
                            <AlertTriangle className="h-4 w-4" />
                          )}
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => handleDeletePost(post.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Confirm Dialog */}
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          onClose={() => setConfirmDialog({ isOpen: false, postId: null, action: null })}
          onConfirm={confirmDelete}
          title="Eliminar Post"
          message="¿Estás seguro de que deseas eliminar este post? Esta acción no se puede deshacer."
          confirmText="Eliminar"
          cancelText="Cancelar"
          type="danger"
        />
      </div>
    </AdminLayout>
  )
}
